<template>
  <div class="container">
    <div class="grid align-items-center">
      <p class="col heading-1 my-4">Find a Sizing</p>
      <div class="col-fixed text-right">
        <Button
          class="p-button-success mr-2"
          @click="
            $router.push({
              name: 'ScenarioInfo',
              params: { sizingId: 'new', scenarioId: 'new' }
            })
          "
        >
          <icon icon="plus-circle" class="mr-1" />
          Create Sizing
        </Button>

        <JSONUploadBtn
          v-if="user.is_admin"
          label="Import Sizing"
          icon="far fa-cloud-upload-alt"
          @submitted="onImport"
        />
      </div>
    </div>

    <SizingsAdvancedSearch v-model:sizings-search="sizingsSearch" />
    <SizingsTable
      v-model:limit="sizingsLimit"
      v-model:offset="sizingsOffset"
      v-model:sort="sizingMultiSortMeta"
      :sizings="sizings"
      :sizings-aggregate-count="sizingsAggregateCount"
      :loading="sizingsFetching"
      :render-links="true"
      class="p-datatable-sm"
    />
  </div>
</template>

<script setup lang="ts">
import type { ExportedSizing } from "@/utils/exports/exportSizing";
import type {
  Sizings_Order_By,
  SizingsWithScenarioIdsQueryVariables
} from "gql/api";
import { useSizingsWithScenarioIdsQuery, Order_By } from "gql/api";

import { ref, computed, watch, onMounted } from "vue";
import { sizingsByCustomerERPId } from "@/models/sizings/predicates";

import useUserInfo from "@/composables/auth/useUserInfo";
import { useRouter, useRoute } from "vue-router";
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import {
  getSizingsSearchFromQuery,
  getSortMetaFromQuery,
  getSortQueryString
} from "@/utils/sizingQueryHelpers";
import useImportSizing from "@/composables/useImportSizing";
import JSONUploadBtn from "@/components/JSONUploadBtn.vue";
import SizingsTable from "@/components/sizings/SizingsTable.vue";
import SizingsAdvancedSearch from "@/components/sizings/SizingsAdvancedSearch.vue";
import {
  type SortMeta,
  getUrqlOrderByMeta
} from "@/composables/primevue/tables";

import { useSizingsAdvancedSearch } from "@/composables/advancedSearch/sizings";
import { watchDebounced } from "@vueuse/core";

const router = useRouter();
const route = useRoute();

const importSizing = useImportSizing();

const user = useUserInfo();

const DEFAULT_SIZING_LIMIT = 20;
const DEFAULT_SIZING_OFFSET = 0;

const sizingsLimit = ref(Number(route.query.limit) || DEFAULT_SIZING_LIMIT);
const sizingsOffset = ref(Number(route.query.offset) || DEFAULT_SIZING_OFFSET);

watch(
  () => route.query.offset,
  newV => {
    sizingsOffset.value = Number(newV) || DEFAULT_SIZING_OFFSET;
  }
);

watch(
  () => route.query.limit,
  newV => {
    sizingsLimit.value = Number(newV) || DEFAULT_SIZING_LIMIT;
  }
);

const { sizingsWhere, sizingsSearch, getSearchRouteQuery } =
  useSizingsAdvancedSearch({
    sizingsSearch: getSizingsSearchFromQuery(route.query),
    sizingsWhere: sizingsByCustomerERPId(
      user.is_customer as boolean,
      user.id as string,
      user.customers as string[]
    )
  });

const sizingMultiSortMeta = ref<SortMeta[] | undefined>(
  getSortMetaFromQuery(route.query)
);

const sizingsOrderBy = ref<Sizings_Order_By[]>(
  sizingMultiSortMeta.value
    ? getUrqlOrderByMeta(sizingMultiSortMeta.value)
    : [{ journal_updated_at: Order_By.Desc }]
);

watch(
  () => route.query.sort,
  () => {
    sizingMultiSortMeta.value = getSortMetaFromQuery(route.query);
    sizingsOrderBy.value = sizingMultiSortMeta.value
      ? getUrqlOrderByMeta(sizingMultiSortMeta.value)
      : [{ journal_updated_at: Order_By.Desc }];
  }
);

const sortIsDefault = computed(() =>
  sizingsOrderBy.value.every(
    ({ journal_updated_at }) => journal_updated_at === Order_By.Desc
  )
);

watch(sizingMultiSortMeta, () => {
  if (sizingMultiSortMeta.value) {
    sizingsOrderBy.value = getUrqlOrderByMeta(sizingMultiSortMeta.value);
  } else {
    sizingsOrderBy.value = [{ journal_updated_at: Order_By.Desc }];
  }
});

const sizingsVariables = computed<SizingsWithScenarioIdsQueryVariables>(() => {
  return {
    withAggregate: true,
    withImportUser: true,
    limit: sizingsLimit.value,
    offset: sizingsOffset.value,
    order_by: sizingsOrderBy.value,
    where: sizingsWhere.value
  };
});

watch(
  () => route.query,
  newQuery => {
    sizingsSearch.value = getSizingsSearchFromQuery(newQuery);
    sizingMultiSortMeta.value = getSortMetaFromQuery(newQuery);
    sizingsOrderBy.value = sizingMultiSortMeta.value
      ? getUrqlOrderByMeta(sizingMultiSortMeta.value)
      : [{ journal_updated_at: Order_By.Desc }];
  }
);

const { fetching: sizingsFetching, ...sizingsWithScenarioIdsQuery } =
  useSizingsWithScenarioIdsQuery({
    variables: sizingsVariables
  });

const sizings = computed(
  () => sizingsWithScenarioIdsQuery.data.value?.sizings ?? []
);
const sizingsAggregateCount = computed(
  () =>
    sizingsWithScenarioIdsQuery.data.value?.sizings_aggregate?.aggregate
      ?.count ?? 0
);

const updateRouterQuery = () => {
  const query: Partial<{
    limit: string;
    offset: string;
    where: string;
    sort: string;
    customer?: string;
    global?: string;
    owner?: string;
    well?: string;
    dateRange?: string;
  }> = {};

  if (sizingsLimit.value !== DEFAULT_SIZING_LIMIT) {
    query.limit = sizingsLimit.value.toString();
  }

  if (sizingsOffset.value !== DEFAULT_SIZING_OFFSET) {
    query.offset = sizingsOffset.value.toString();
  }

  if (!sortIsDefault.value && sizingMultiSortMeta.value) {
    query.sort = getSortQueryString(sizingMultiSortMeta.value);
  }

  const searchQuery = getSearchRouteQuery();
  Object.assign(query, searchQuery || {});

  router.push({
    query
  });
};

watchDebounced(
  [
    sizingsLimit,
    sizingsOffset,
    sizingsSearch,
    sizingsWhere,
    sizingsOrderBy,
    sizingMultiSortMeta
  ],
  () => {
    updateRouterQuery();
  },
  { deep: true, debounce: 400 }
);

const onImport = async (exportJSON: ExportedSizing) => {
  if ("payload" in exportJSON) {
    const importedSizing = await importSizing(exportJSON);

    await haltUntilCompletion();
    router.push({
      name: "Sizing",
      params: {
        sizingId: importedSizing?.id
      }
    });
  } else {
    alert("invalid import JSON");
  }
};

onMounted(() => {
  updateRouterQuery();
});
</script>
